import { useState, useRef, FocusEvent, ChangeEvent, MouseEvent, useEffect, useCallback } from 'react';
import {
  Editor,
  EditorState,
  RichUtils,
  ContentState,
  Modifier,
  convertToRaw,
  convertFromRaw,
  genKey,
  RawDraftContentState,
} from 'draft-js';
import { convertFromHTML, convertToHTML } from 'draft-convert';
import cn from 'classnames';

import 'draft-js/dist/Draft.css';
import styles from './TextEditor.module.scss';

interface ITextEditorProps {
  value?: string;
  placeholder?: string;
  error?: string;
  onChange?: (value: ChangeEvent<HTMLTextAreaElement> | string) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
}

// @ts-ignore
interface ITextEditorRef extends Editor {
  editor: { firstChild: { innerHTML: string } };
}

const TEXT_COLORS = {
  gray: { color: 'gray' },
  lightgray: { color: 'lightgray' },
  red: { color: 'red' },
  darkorange: { color: 'darkorange' },
  yellow: { color: 'yellow' },
  yellowgreen: { color: 'yellowgreen' },
  chartreuse: { color: 'chartreuse' },
  paleturquoise: { color: 'paleturquoise' },
  aqua: { color: 'aqua' },
  thistle: { color: 'thistle' },
  plum: { color: 'plum' },
  dimgray: { color: 'dimgray' },
  silver: { color: 'silver' },
  crimson: { color: 'crimson' },
  coral: { color: 'coral' },
  gold: { color: 'gold' },
  darkseagreen: { color: 'darkseagreen' },
  limegreen: { color: 'limegreen' },
  darkturquoise: { color: 'darkturquoise' },
  dodgerblue: { color: 'dodgerblue' },
  blueviolet: { color: 'blueviolet' },
  magenta: { color: 'magenta' },
  black: { color: 'black' },
  darkgray: { color: 'darkgray' },
  darkred: { color: 'darkred' },
  lightsalmon: { color: 'lightsalmon' },
  orange: { color: 'orange' },
  olive: { color: 'olive' },
  darkgreen: { color: 'darkgreen' },
  cadetblue: { color: 'cadetblue' },
  blue: { color: 'blue' },
  indigo: { color: 'indigo' },
  purple: { color: 'purple' },
};

const TEXT_STYLES = [
  { style: 'BOLD', type: 'font', className: styles.BoldText },
  { style: 'ITALIC', type: 'font', className: styles.ItalicText },
  { style: 'UNDERLINE', type: 'font', className: styles.UnderlineText },
  { style: 'ordered-list-item', type: 'list', className: styles.NumberedList },
  { style: 'unordered-list-item', type: 'list', className: styles.UnnumberedList },
];

const convertContentState = (rawContentState: RawDraftContentState) =>
  convertFromRaw({
    entityMap: rawContentState.entityMap,
    blocks: rawContentState.blocks.map(item => ({ ...item, key: genKey() })),
  });

const convertToStateContent = (value: string | undefined) => {
  if (value) {
    if (!new RegExp(/<\/?[a-z][\s\S]*>/i).test(value)) {
      return EditorState.createWithContent(ContentState.createFromText(value));
    }
    const contentState = convertFromHTML({
      // @ts-ignore 'currentStyle' argument is missing in @types/draft-convert
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      htmlToStyle: (nodeName: string, node: HTMLElement, currentStyle: any) => {
        if (nodeName === 'span' && node.style.color) {
          return currentStyle.add(`${node.style.color}`);
        }

        return currentStyle;
      },
    })(value);
    const convertToRawHtml = convertToRaw(contentState);

    return EditorState.createWithContent(convertContentState(convertToRawHtml));
  }

  const convertToRawFromText = convertToRaw(ContentState.createFromText(''));

  return EditorState.createWithContent(convertContentState(convertToRawFromText));
};

export const TextEditor = ({ value, placeholder, error, onBlur, onChange }: ITextEditorProps) => {
  const [editorState, setEditorState] = useState(convertToStateContent(value));
  const [isFocus, setIsFocus] = useState(false);
  const [isOpenPicker, setIsOpenPicker] = useState(false);
  const ref = useRef<ITextEditorRef>(null);
  const currentStyle = editorState.getCurrentInlineStyle();
  const currentColor = Object.keys(TEXT_COLORS).find((el: string) => editorState.getCurrentInlineStyle().has(el));

  useEffect(() => {
    setEditorState(convertToStateContent(value));
  }, [value]);

  const textValue = editorState.getCurrentContent().getPlainText('\u0001');
  const wrapperClassName = cn(styles.TextEditor, {
    [styles.FocusedEditor]: !error && isFocus,
    [styles.WithErrorEditor]: error && !isFocus,
    [styles.FocusedErrorEditor]: error && isFocus,
  });
  const isRenderPlaceholder = editorState.getCurrentContent().getBlockMap().first().getType() === 'unstyled';

  const getActiveStyle = (style: string, type: string) => {
    if (type === 'font') {
      return currentStyle.has(style);
    }

    return style === RichUtils.getCurrentBlockType(editorState);
  };

  const toggleStyles = (e: MouseEvent, style: string, styleType: string) => {
    e.preventDefault();

    if (styleType === 'font') {
      if (!isFocus) {
        return;
      }

      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    } else {
      setEditorState(RichUtils.toggleBlockType(editorState, style));
    }
  };

  const toggleColor = useCallback(
    (e: MouseEvent, textColor: string) => {
      e.preventDefault();

      const selection = editorState.getSelection();
      const nextContentState = Object.keys(TEXT_COLORS).reduce((contentState, color) => {
        return Modifier.removeInlineStyle(contentState, selection, color);
      }, editorState.getCurrentContent());
      let nextEditorState = EditorState.push(editorState, nextContentState, 'change-inline-style');

      if (selection.isCollapsed()) {
        nextEditorState = currentStyle.reduce((state: EditorState, color: string) => {
          return RichUtils.toggleInlineStyle(state, color);
        }, nextEditorState);
      }

      if (!currentStyle.has(textColor)) {
        nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, textColor);
      }

      setEditorState(nextEditorState);
      setIsOpenPicker(false);
    },
    [currentStyle, editorState],
  );

  const handleColorButton = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setIsOpenPicker(!isOpenPicker);
    },
    [isOpenPicker],
  );

  const onColorClick = useCallback((e: MouseEvent) => e.preventDefault(), []);

  const stylesControls = () => {
    return (
      <div className={styles.ButtonWrapper}>
        {TEXT_STYLES.map(
          ({ style, type, className }): Object => {
            function onStyleChange(e: MouseEvent) {
              toggleStyles(e, style, type);
            }

            return (
              <div
                key={style}
                className={styles.ButtonContainer}
                style={getActiveStyle(style, type) ? { backgroundColor: '#999' } : undefined}
                onMouseDown={onStyleChange}
              >
                <span className={className} />
              </div>
            );
          },
        )}
        <div className={styles.ColorPickerWrapper}>
          <div
            className={styles.ColorButton}
            style={{ backgroundColor: `${currentColor || '#555'}` }}
            onMouseDown={handleColorButton}
          />
          {isOpenPicker ? (
            <div className={styles.ColorPicker} onMouseDown={onColorClick}>
              {Object.keys(TEXT_COLORS).map((color: string) => {
                function onColorChange(e: MouseEvent) {
                  toggleColor(e, color);
                }

                return (
                  <div
                    key={color}
                    style={{ backgroundColor: color }}
                    className={styles.ColorPickerItem}
                    onMouseDown={onColorChange}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const handleChange = useCallback((data: EditorState) => {
    setEditorState(data);
  }, []);

  const handleClick = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    ref.current?.focus();
    setIsFocus(true);
  }, []);

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      const htmlContent = convertToHTML({
        styleToHTML: (style: string) => {
          if (style in TEXT_COLORS) {
            return { start: `<span style="color: ${style}">`, end: '</span>' };
          }

          return undefined;
        },
      })(editorState.getCurrentContent());

      setIsFocus(false);
      setIsOpenPicker(false);

      if (onChange) {
        textValue && htmlContent ? onChange(htmlContent) : onChange('');
      }

      if (onBlur) {
        onBlur(e);
      }
    },
    [editorState, onBlur, onChange, textValue],
  );

  return (
    <div className={wrapperClassName} onClick={handleClick}>
      {stylesControls()}
      <Editor
        // @ts-ignore
        ref={ref}
        customStyleMap={TEXT_COLORS}
        editorState={editorState}
        placeholder={isRenderPlaceholder ? placeholder : undefined}
        onBlur={handleBlur}
        onChange={handleChange}
        editorKey="editor"
      />
    </div>
  );
};
